import React, { useEffect } from 'react';

import cx from 'classnames';

import { uploadMusicTypes } from '@/types';
import { songConstants, iconConstants } from '@/constants';
import Icons from '@/icons';
import { generalHelpers } from '@/helpers';
import { Button, Dropdown, Input, Textarea } from '@/kaya';
import { AudioBox } from '@/component';
import { useArtistStore } from '@/store';

import {
  getAudioDuration,
  buildGetUrlsPayload,
  buildSongUploadPayload,
  validateSongDetails,
} from '@/helpers/uploadMusic.helper';
import { getSongUploadUrlsAction, uploadSongAction } from '@/actions/song.actions';
import { get } from 'lodash';

import styles from './songsDetailsPopup.module.scss';

interface SongsDetailsPopupProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  handleChange: (id: string | object) => (val: string | object | boolean | number) => void;
  setIsUploadDocument2: (visible: boolean) => void;
  formvalues: uploadMusicTypes.UploadMusic;
  setErrors: React.Dispatch<
    React.SetStateAction<{
      [key: string]: string;
    }>
  >;
  errors: { [key: string]: string };
}

const SongsDetailsPopup = (props: SongsDetailsPopupProps) => {
  const { setVisible, handleChange, setIsUploadDocument2, formvalues, setErrors, errors } = props;

  const { artist } = useArtistStore();

  const inputRef = React.useRef<HTMLInputElement>(null);
  const isSomethingChanged = React.useRef(false);
  const [selectedOption, setSelectedOption] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);

  useEffect(() => {
    if (formvalues.media && formvalues.duration === 0) {
      getAudioDuration(formvalues.media)
        .then((dur) => {
          handleChange('duration')(dur);
        })
        .catch((err) => {
          console.error('Error getting duration:', err);
        });
    }
  }, [formvalues.media, handleChange, formvalues.duration]);

  const hadnleImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    isSomethingChanged.current = true;

    handleChange({
      thumbnail_image: file,
      thumbnail_img: URL.createObjectURL(file || ({} as File)),
    })('');

    setErrors({
      ...errors,
      thumbnail_image: '',
    });
  };

  const handleNextClick = async () => {
    const err = validateSongDetails(formvalues);

    if (!generalHelpers.isEmptyErrors(err)) {
      setErrors(err);
      return;
    }

    if (selectedOption === 'co_owner') {
      setIsUploadDocument2(true);
      setVisible(false);
    } else if (selectedOption === 'sole_owner') {
      const urlsPayload = buildGetUrlsPayload(formvalues, false);
      setIsLoading(true);
      const urlsRes = await getSongUploadUrlsAction({ payload: urlsPayload });
      if (!urlsRes.success) {
        setIsLoading(false);
        alert(`Failed to get upload URLs: ${urlsRes.data.message || JSON.stringify(urlsRes.data)}`);
        return;
      }
      const { song_id, signed_urls, keys } = urlsRes.data;

      let uploadSuccess = true;

      // Upload thumbnail
      if (formvalues.thumbnail_image) {
        try {
          const thumbRes = await fetch(signed_urls.thumbnail, {
            method: 'PUT',
            headers: {
              'Content-Type': formvalues.thumbnail_image.type,
            },
            body: formvalues.thumbnail_image,
          });
          if (!thumbRes.ok) {
            throw new Error(`Thumbnail upload failed: ${thumbRes.status}`);
          }
        } catch (e) {
          console.error('Thumbnail upload error:', e);
          uploadSuccess = false;
        }
      }

      // Upload audio
      try {
        const audioRes = await fetch(signed_urls.audio, {
          method: 'PUT',
          headers: {
            'Content-Type': formvalues.media!.type,
          },
          body: formvalues.media,
        });
        if (!audioRes.ok) {
          throw new Error(`Audio upload failed: ${audioRes.status}`);
        }
      } catch (e) {
        console.error('Audio upload error:', e);
        uploadSuccess = false;
      }

      if (!uploadSuccess) {
        setIsLoading(false);
        alert('One or more file uploads failed. Please try again.');
        return;
      }

      // Step 2: Create song
      const uploadPayload = buildSongUploadPayload(formvalues, song_id, keys, false);
      const uploadRes = await uploadSongAction({ payload: uploadPayload });
      setIsLoading(false);
      if (uploadRes.success) {
        const url = get(uploadRes, 'data.url', '');
        window.open(url, '_self');
      } else {
        alert(`Failed to create song: ${uploadRes.data.message || JSON.stringify(uploadRes.data)}`);
      }
    }
  };

  const handleRemove = () => {
    isSomethingChanged.current = true;
    handleChange({
      thumbnail_image: '',
      thumbnail_img: '',
    })('');
  };

  useEffect(() => {
    handleChange({
      artist_name: artist.stage_name,
      title: formvalues.media?.name.split('-')[0] || '',
    })('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={styles.songDetailsPopup}
      onClick={() => {
        if (isSomethingChanged.current) {
          return;
        }
        setVisible(false);
        setErrors({});
      }}
    >
      <div
        className={styles.songDetails}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <p className={styles.details}>Details</p>
        <div className={styles.container}>
          <div className={styles.left}>
            <div>
              <p className={styles.title}>Title</p>
              <Input
                // placeholder="Title"
                width="480px"
                onChange={(val) => {
                  isSomethingChanged.current = true;
                  handleChange('title')(val);
                }}
                errors={errors}
                id="title"
                value={formvalues.title}
              />
            </div>
            <div>
              <p className={styles.title}>Description</p>
              <Textarea
                // placeHolder="Description"
                width="480px"
                onChange={(val) => {
                  isSomethingChanged.current = true;
                  handleChange('description')(val);
                }}
                error={errors.description}
              />
            </div>
            <div>
              <p className={styles.title}>Artist Name</p>
              <Input
                // placeholder="Artist Name"
                width="480px"
                onChange={(val) => {
                  isSomethingChanged.current = true;
                  handleChange('artist_name')(val);
                }}
                errors={errors}
                id="artist_name"
                value={formvalues.artist_name}
              />
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                // width: '100%',
              }}
            >
              <div>
                <p className={styles.title}>Primary Genre</p>
                <Dropdown
                  placeholder="Primary Genre"
                  // width="480px"
                  width="225px"
                  options={songConstants.GENRE}
                  onMenuItemClick={(val) => {
                    isSomethingChanged.current = true;
                    handleChange('genre')(val);
                  }}
                  errors={errors}
                  id="genre"
                />
              </div>
              <div>
                <p className={styles.title}>Secondary Genre (Optional)</p>
                <Dropdown
                  placeholder="Secondary Genre"
                  // width="480px"
                  width="225px"
                  options={songConstants.GENRE}
                  onMenuItemClick={(val) => {
                    isSomethingChanged.current = true;
                    handleChange('second_genre')(val);
                  }}
                  id="second_genre"
                />
              </div>
            </div>
            <div>
              <p className={styles.title}>🎶 Crew Credits (Optional)</p>
              <Textarea
                placeHolder={
                  'Got some legends behind the track? 🎸 Drop a shoutout to your writer, ' +
                  'drummer, guitarist, or anyone else who brought the magic! Let’s give ' +
                  'them the spotlight they deserve! 🌟'
                }
                width="480px"
                onChange={(val) => {
                  isSomethingChanged.current = true;
                  handleChange('crew_credits')(val);
                }}
                error={errors.description}
              />
            </div>
          </div>
          <div>
            {formvalues.thumbnail_img ? (
              <div
                onClick={() => {
                  inputRef.current?.click();
                }}
                className={styles.img}
              >
                <img src={formvalues.thumbnail_img} alt="thumbnail" className={styles.thumbnail} />

                <div className={styles.close} onClick={handleRemove}>
                  <Icons type={iconConstants.ICONS.CLOSE_ICON} />
                </div>
              </div>
            ) : (
              <div className={cx(styles.uploadImg, { [styles.imgError]: errors.thumbnail_image })}>
                <Icons type={iconConstants.ICONS.ADD_IMAGE} className={styles.uploadIcon} />
                <p className={styles.Thumbnail}>Add Thumbnail </p>
                <p className={styles.imgs}>JPEG & PNG file only </p>
                <Button
                  type="Outline"
                  width="100px"
                  onClick={() => {
                    inputRef.current?.click();
                  }}
                >
                  Upload
                </Button>
                <input
                  type="file"
                  accept="image/png, image/jpeg"
                  style={{ display: 'none' }}
                  ref={inputRef}
                  onChange={hadnleImage}
                />
              </div>
            )}
            <AudioBox formvalues={formvalues} />
          </div>
        </div>
        <div className={styles.radioButtonContainer}>
          <div className={styles.container}>
            <input
              type="radio"
              name="ownership"
              value="sole_owner"
              id="sole_owner"
              checked={selectedOption === 'sole_owner'}
              onChange={() => {
                setSelectedOption('sole_owner');
                // handleChange('sole_owner')(true);
              }}
            />
            <p>
              {'I confirm that I am the sole owner of all rights to this song, including writing, composition, and recording rights, ' +
                'and that I have full authority to upload and distribute this track on the 4Play platform. I acknowledge that I am ' +
                'responsible for any claims or disputes related to copyright ownership.'}{' '}
            </p>
          </div>
          <div className={styles.container}>
            <input
              type="radio"
              name="ownership"
              value="co_owner"
              id="co_owner"
              checked={selectedOption === 'co_owner'}
              onChange={() => setSelectedOption('co_owner')}
            />
            <p>
              I am not the sole owner of the rights to this song, but I have obtained all necessary
              permissions from all rights holders.
            </p>
          </div>
        </div>
        <Button
          type="Primary"
          width="200px"
          onClick={handleNextClick}
          disabled={isLoading}
          isLoading={isLoading}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default SongsDetailsPopup;
